/**
 * Accounts collection — typed CRUD on top of the generic store.
 *
 * Wraps `db` so views deal in `Account` records, and so we can
 * centralise things like timestamp stamping, audit-trail appending,
 * and seed bootstrap. When we cut over to Firestore the only thing
 * this file changes is the import in `store.ts`.
 */

import { db, nowIso, uuid } from './store';
import type {
  Account,
  AccountWorkflow,
  AuditEntry,
  EmailEntry,
  PipelineStage,
} from './types';
import { seedAccounts } from './seed';

export async function listAccounts(): Promise<Account[]> {
  return db.list('accounts');
}

export async function getAccount(id: string): Promise<Account | null> {
  return db.get('accounts', id);
}

export type NewAccountInput = Partial<
  Omit<Account, 'id' | 'createdAt' | 'updatedAt' | 'audit' | 'emails' | 'workflows'>
> & {
  name: string;
  workflows?: AccountWorkflow[];
  audit?: AuditEntry[];
  emails?: EmailEntry[];
};

export async function createAccount(input: NewAccountInput): Promise<Account> {
  const ts = nowIso();
  const record: Account = {
    name: input.name,
    pocName: input.pocName,
    pocTitle: input.pocTitle,
    pocEmail: input.pocEmail,
    pocPhone: input.pocPhone,
    ownerName: input.ownerName,
    assignedTo: input.assignedTo,
    workflows: input.workflows ?? [],
    notes: input.notes,
    audit: input.audit ?? [
      { id: uuid(), at: ts, actor: 'You', text: 'Account created' },
    ],
    emails: input.emails ?? [],
    id: uuid(),
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('accounts', record);
  return record;
}

/** Add a new workflow to an existing account. Appends an audit entry. */
export async function addWorkflow(
  accountId: string,
  workflow: Omit<AccountWorkflow, 'id'>,
  actor = 'You',
): Promise<Account> {
  const existing = await db.get('accounts', accountId);
  if (!existing) throw new Error(`No account ${accountId}`);
  const ts = nowIso();
  const wf: AccountWorkflow = { id: uuid(), ...workflow };
  const audit: AuditEntry[] = [
    ...existing.audit,
    {
      id: uuid(),
      at: ts,
      actor,
      text: `Added workflow "${wf.name}" (${wf.stage})`,
    },
  ];
  return db.update('accounts', accountId, {
    workflows: [...existing.workflows, wf],
    audit,
    updatedAt: ts,
  });
}

/**
 * Patch an account by id. Always re-stamps `updatedAt`. The caller is
 * expected to attach an audit entry for stage transitions via
 * `moveWorkflowStage`; for arbitrary edits we append a single generic
 * "Edited account" entry only when no audit list is supplied.
 */
export async function updateAccount(
  id: string,
  patch: Partial<Omit<Account, 'id' | 'createdAt'>>,
): Promise<Account> {
  return db.update('accounts', id, { ...patch, updatedAt: nowIso() });
}

/**
 * Move a single workflow on an account to a new stage. The kanban
 * board operates per-workflow, so this is what the drag-and-drop
 * handler ultimately calls. Appends an audit entry on the parent
 * account so the move is captured in the audit timeline.
 */
export async function moveWorkflowStage(
  accountId: string,
  workflowId: string,
  to: PipelineStage,
  actor = 'You',
): Promise<Account> {
  const existing = await db.get('accounts', accountId);
  if (!existing) throw new Error(`No account ${accountId}`);
  const wf = existing.workflows.find((w) => w.id === workflowId);
  if (!wf) throw new Error(`No workflow ${workflowId} on account ${accountId}`);
  if (wf.stage === to) return existing;
  const ts = nowIso();
  const workflows = existing.workflows.map((w) =>
    w.id === workflowId ? { ...w, stage: to } : w,
  );
  const audit: AuditEntry[] = [
    ...existing.audit,
    {
      id: uuid(),
      at: ts,
      actor,
      text: `Moved workflow "${wf.name}": ${wf.stage} → ${to}`,
    },
  ];
  return db.update('accounts', accountId, {
    workflows,
    audit,
    updatedAt: ts,
  });
}

/** Append a free-form audit entry — used by the detail view. */
export async function appendAudit(
  id: string,
  text: string,
  actor = 'You',
): Promise<Account> {
  const existing = await db.get('accounts', id);
  if (!existing) throw new Error(`No account ${id}`);
  const ts = nowIso();
  const audit: AuditEntry[] = [
    ...existing.audit,
    { id: uuid(), at: ts, actor, text },
  ];
  return db.update('accounts', id, { audit, updatedAt: ts });
}

/** Append an email-trail entry. */
export async function appendEmail(
  id: string,
  email: Omit<EmailEntry, 'id'>,
): Promise<Account> {
  const existing = await db.get('accounts', id);
  if (!existing) throw new Error(`No account ${id}`);
  const ts = nowIso();
  const emails: EmailEntry[] = [...existing.emails, { id: uuid(), ...email }];
  return db.update('accounts', id, { emails, updatedAt: ts });
}

export async function deleteAccount(id: string): Promise<void> {
  await db.remove('accounts', id);
}

export function subscribeAccounts(fn: (records: Account[]) => void): () => void {
  return db.subscribe('accounts', fn);
}

/**
 * Seed the accounts collection with the rows from the Client Lifecycle
 * CSV on first load. Idempotent: only runs when the collection is
 * empty.
 */
export async function bootstrapAccountsIfEmpty(): Promise<void> {
  const existing = await db.list('accounts');
  if (existing.length > 0) return;
  await db.replaceAll('accounts', seedAccounts);
}

/** Reset the accounts collection back to seed data. Useful for dev. */
export async function resetAccountsToSeed(): Promise<void> {
  await db.replaceAll('accounts', seedAccounts);
}
